/**
 * RoomTable — buyer-side rooms display.
 *
 * Desktop: real <table> with sortable headers.
 * Mobile (≤ 600px): stacked card list grouped by level.
 *
 * Hidden entirely when the listing has no rooms — per design spec §3.3 we
 * don't want every legacy listing to show an empty-state warning.
 *
 * Props:
 *  - rooms: Room[] — from the house API response.
 */

import React, { useMemo, useState } from 'react';
import {
  LEVEL_ORDER_INDEX,
  TYPE_PIN_ORDER,
  roomTypeLabel,
} from '../SellerDashboard/roomConstants';
import { MLS_DATA_DISCLAIMER } from '../../utils/disclaimers';
import './RoomTable.css';

const SORT_OPTIONS = [
  { value: 'level-type', label: 'Level → Type' },
  { value: 'type-level', label: 'Type → Level' },
  { value: 'largest', label: 'Largest first' },
  { value: 'smallest', label: 'Smallest first' },
];

const computeArea = (room) => {
  const l = parseFloat(room.length_ft);
  const w = parseFloat(room.width_ft);
  if (Number.isNaN(l) || Number.isNaN(w)) return 0;
  return Math.round(l * w);
};

const dimDisplay = (n) => {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return '—';
  return v.toFixed(1);
};

const sortRooms = (rooms, key, dir = 'asc') => {
  const data = rooms.map((r, i) => ({ ...r, _area: computeArea(r), _pos: i }));
  data.sort((a, b) => {
    if (key === 'largest' || key === 'smallest') {
      const cmp = b._area - a._area;
      return key === 'largest' ? cmp : -cmp;
    }
    if (key === 'type') {
      return roomTypeLabel(a.room_type).localeCompare(roomTypeLabel(b.room_type));
    }
    if (key === 'area') {
      return a._area - b._area;
    }
    if (key === 'type-level') {
      const tCmp = roomTypeLabel(a.room_type).localeCompare(roomTypeLabel(b.room_type));
      if (tCmp !== 0) return tCmp;
      return (LEVEL_ORDER_INDEX[a.level] ?? 99) - (LEVEL_ORDER_INDEX[b.level] ?? 99);
    }
    // Default: level then pinned-type-order then alphabetical type.
    const lCmp = (LEVEL_ORDER_INDEX[a.level] ?? 99) - (LEVEL_ORDER_INDEX[b.level] ?? 99);
    if (lCmp !== 0) return lCmp;
    const aPin = TYPE_PIN_ORDER[a.room_type] ?? 99;
    const bPin = TYPE_PIN_ORDER[b.room_type] ?? 99;
    if (aPin !== bPin) return aPin - bPin;
    return roomTypeLabel(a.room_type).localeCompare(roomTypeLabel(b.room_type));
  });
  if (dir === 'desc' && key !== 'largest' && key !== 'smallest') data.reverse();
  return data;
};

const groupByLevel = (rooms) => {
  const groups = {};
  rooms.forEach((r) => {
    const key = r.level || 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });
  return Object.entries(groups);
};

export default function RoomTable({ rooms }) {
  // Hooks MUST run before any conditional return — call them up front,
  // then bail out when the data is empty.
  const [sortKey, setSortKey] = useState('level');
  const [sortDir, setSortDir] = useState('asc');
  // Mobile-only convenience sort selector — falls back to a level grouping.
  const [mobileSort, setMobileSort] = useState('level-type');

  const validRooms = useMemo(() => {
    if (!Array.isArray(rooms)) return [];
    return rooms.filter(
      (r) =>
        r &&
        parseFloat(r.length_ft) > 0 &&
        parseFloat(r.width_ft) > 0,
    );
  }, [rooms]);

  const totals = useMemo(() => {
    let count = validRooms.length;
    let sqft = 0;
    validRooms.forEach((r) => {
      sqft += computeArea(r);
    });
    return { count, sqft };
  }, [validRooms]);

  const desktopRooms = useMemo(
    () => sortRooms(validRooms, sortKey, sortDir),
    [validRooms, sortKey, sortDir],
  );

  const mobileRoomGroups = useMemo(() => {
    if (mobileSort === 'largest' || mobileSort === 'smallest') {
      const sorted = sortRooms(validRooms, mobileSort);
      return [['All rooms', sorted]];
    }
    if (mobileSort === 'type-level') {
      const sorted = sortRooms(validRooms, 'type-level');
      const groups = {};
      sorted.forEach((r) => {
        const key = roomTypeLabel(r.room_type) || 'Other';
        if (!groups[key]) groups[key] = [];
        groups[key].push(r);
      });
      return Object.entries(groups);
    }
    // level-type (default)
    return groupByLevel(sortRooms(validRooms, 'level'));
  }, [validRooms, mobileSort]);

  if (!Array.isArray(rooms) || validRooms.length === 0) {
    return null;
  }

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const ariaSort = (key) => {
    if (sortKey !== key) return 'none';
    return sortDir === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <section className="room-table-section" aria-labelledby="room-table-title" data-testid="room-table">
      <header className="room-table-section__head">
        <h2 id="room-table-title">Rooms</h2>
        <span
          className="room-table-section__summary"
          data-testid="room-table-summary"
        >
          {totals.count} {totals.count === 1 ? 'room' : 'rooms'} · {totals.sqft.toLocaleString()} sf
        </span>
      </header>
      <p className="room-table-section__disclaimer">{MLS_DATA_DISCLAIMER}</p>

      {/* Desktop table */}
      <div className="room-table-section__desktop">
        <table className="room-table">
          <thead>
            <tr>
              <th scope="col" aria-sort={ariaSort('level')}>
                <button
                  type="button"
                  className="room-table__sort"
                  onClick={() => toggleSort('level')}
                >
                  Level{sortKey === 'level' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </button>
              </th>
              <th scope="col" aria-sort={ariaSort('type')}>
                <button
                  type="button"
                  className="room-table__sort"
                  onClick={() => toggleSort('type')}
                >
                  Type{sortKey === 'type' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </button>
              </th>
              <th scope="col">Dimensions</th>
              <th scope="col" aria-sort={ariaSort('area')}>
                <button
                  type="button"
                  className="room-table__sort"
                  onClick={() => toggleSort('area')}
                >
                  Area{sortKey === 'area' && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                </button>
              </th>
              <th scope="col">Flooring</th>
            </tr>
          </thead>
          <tbody>
            {desktopRooms.map((r, i) => (
              <tr key={r.id != null ? `id-${r.id}` : `pos-${i}`}>
                <td>{r.level || '—'}</td>
                <td>
                  {roomTypeLabel(r.room_type) || '—'}
                  {r.features && (
                    <span
                      className="room-table__features"
                      title={r.features}
                    >
                      {' '}<span aria-hidden="true">ℹ︎</span>
                      <span className="room-table__features-text">{r.features}</span>
                    </span>
                  )}
                </td>
                <td
                  aria-label={`${dimDisplay(r.length_ft)} by ${dimDisplay(r.width_ft)} feet`}
                >
                  {dimDisplay(r.length_ft)} × {dimDisplay(r.width_ft)} ft
                </td>
                <td>{computeArea(r)} sf</td>
                <td>{r.flooring || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="room-table-section__mobile">
        <div className="room-table-section__sort-row">
          <label
            className="room-table-section__sort-label"
            htmlFor="room-table-mobile-sort"
          >
            Sort
          </label>
          <select
            id="room-table-mobile-sort"
            className="room-table-section__sort-select"
            value={mobileSort}
            onChange={(e) => setMobileSort(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {mobileRoomGroups.map(([groupLabel, groupRooms], groupIdx) => (
          <div
            key={groupLabel}
            className="room-table-section__group"
            aria-labelledby={`rt-group-${groupIdx}`}
          >
            <h3
              id={`rt-group-${groupIdx}`}
              className="room-table-section__group-title"
            >
              {groupLabel}
            </h3>
            <ul className="room-table-section__cards">
              {groupRooms.map((r, i) => (
                <li
                  key={r.id != null ? `id-${r.id}` : `${groupLabel}-${i}`}
                  className="room-table-section__card"
                >
                  <div className="room-table-section__card-head">
                    <span className="room-table-section__card-type">
                      {roomTypeLabel(r.room_type) || '—'}
                    </span>
                    <span className="room-table-section__card-area">
                      {computeArea(r)} sf
                    </span>
                  </div>
                  <div className="room-table-section__card-meta">
                    <span>
                      {dimDisplay(r.length_ft)} × {dimDisplay(r.width_ft)} ft
                    </span>
                    {r.flooring && <span> · {r.flooring}</span>}
                  </div>
                  {r.features && (
                    <div className="room-table-section__card-features">
                      <span aria-hidden="true">ℹ︎</span> {r.features}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
